import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { DEMO_PROTOCOL_DEVIATIONS } from '../api/demoData';

const OPEN_DEVIATION_COUNT = DEMO_PROTOCOL_DEVIATIONS.filter((d) => d.status === 'Open').length;

interface NavItem {
  to: string;
  label: string;
  icon: string;
  badge: number;
}

const ALL_NAV_ITEMS: NavItem[] = [
  { to: '/how-it-works', label: 'How It Works', icon: '\uD83D\uDCA1', badge: 0 },
  { to: '/protocol-setup', label: 'Protocol Setup', icon: '\uD83D\uDCCB', badge: 0 },
  { to: '/budget', label: 'Budget', icon: '\uD83D\uDCB2', badge: 0 },
  { to: '/forms', label: 'Form Generator', icon: '\uD83D\uDCDD', badge: 0 },
  { to: '/fhir-sim', label: 'FHIR Simulator', icon: '\u26A1', badge: 0 },
  { to: '/swimlane', label: 'Data Flow', icon: '\uD83D\uDD00', badge: 0 },
  { to: '/visit-timeline', label: 'Visit Timeline', icon: '\uD83D\uDCC5', badge: 0 },
  { to: '/data-sources', label: 'Data Sources', icon: '\uD83D\uDD0C', badge: 0 },
  { to: '/evidence', label: 'Evidence Timeline', icon: '\u23F1', badge: 0 },
  { to: '/patient-twin', label: 'Patient Twin', icon: '\uD83E\uDDEC', badge: 0 },
  { to: '/safety', label: 'Safety Dashboard', icon: '\u26A0', badge: 0 },
  { to: '/decisions', label: 'Decision Twins', icon: '\u2693', badge: 0 },
  { to: '/deviations', label: 'Protocol Deviations', icon: '\u26A0\uFE0F', badge: OPEN_DEVIATION_COUNT },
  { to: '/ip', label: 'IP Accountability', icon: '\uD83D\uDC8A', badge: 0 },
  { to: '/samples', label: 'Sample Log', icon: '\uD83E\uDDEA', badge: 0 },
  { to: '/sae', label: 'SAE Clock', icon: '\u23F0', badge: 0 },
  { to: '/validation', label: 'Validation', icon: '\uD83D\uDEE1', badge: 0 },
];

const COORDINATOR_NAV: NavItem[] = [
  { to: '/home/coordinator', label: 'Home', icon: '\uD83C\uDFE0', badge: 0 },
  { to: '/forms', label: 'Form Generator', icon: '\uD83D\uDCDD', badge: 0 },
  { to: '/visit-timeline', label: 'Visit Timeline', icon: '\uD83D\uDCC5', badge: 0 },
  { to: '/safety', label: 'Patients', icon: '\u26A0', badge: 0 },
  { to: '/evidence', label: 'Visit Data', icon: '\u23F1', badge: 0 },
  { to: '/samples', label: 'Sample Log', icon: '\uD83E\uDDEA', badge: 0 },
  { to: '/deviations', label: 'Protocol Deviations', icon: '\u26A0\uFE0F', badge: OPEN_DEVIATION_COUNT },
  { to: '/ip', label: 'IP Accountability', icon: '\uD83D\uDC8A', badge: 0 },
  { to: '/sae', label: 'SAE Clock', icon: '\u23F0', badge: 0 },
];

const MONITOR_NAV: NavItem[] = [
  { to: '/home/monitor', label: 'Home', icon: '\uD83C\uDFE0', badge: 0 },
  { to: '/fhir-sim', label: 'FHIR Simulator', icon: '\u26A1', badge: 0 },
  { to: '/swimlane', label: 'Data Flow', icon: '\uD83D\uDD00', badge: 0 },
  { to: '/evidence', label: 'SDV Queue', icon: '\u23F1', badge: 0 },
  { to: '/safety', label: 'Site Risk Monitor', icon: '\u26A0', badge: 0 },
  { to: '/samples', label: 'Sample Log', icon: '\uD83E\uDDEA', badge: 0 },
  { to: '/deviations', label: 'Protocol Deviations', icon: '\u26A0\uFE0F', badge: OPEN_DEVIATION_COUNT },
  { to: '/decisions', label: 'Decision Twins', icon: '\u2693', badge: 0 },
  { to: '/sae', label: 'SAE Clock', icon: '\u23F0', badge: 0 },
  { to: '/validation', label: 'Validation', icon: '\uD83D\uDEE1', badge: 0 },
];

const BUDGET_NAV: NavItem[] = [
  { to: '/home/budget', label: 'Home', icon: '\uD83C\uDFE0', badge: 0 },
  { to: '/budget', label: 'Budget Calculator', icon: '\uD83D\uDCB2', badge: 0 },
  { to: '/protocol-setup', label: 'Protocol Setup', icon: '\uD83D\uDCCB', badge: 0 },
  { to: '/safety', label: 'Safety Dashboard', icon: '\u26A0', badge: 0 },
  { to: '/samples', label: 'Sample Log', icon: '\uD83E\uDDEA', badge: 0 },
];

function getRole(): string | null {
  try {
    return localStorage.getItem('d3ri_selected_role');
  } catch {
    return null;
  }
}

export default function Sidebar() {
  const navigate = useNavigate();
  const role = getRole();
  const items = role === 'coordinator' ? COORDINATOR_NAV : role === 'monitor' ? MONITOR_NAV : role === 'budget' ? BUDGET_NAV : ALL_NAV_ITEMS;

  const userName = localStorage.getItem('d3ri_user_name') || '';
  const userEmail = localStorage.getItem('d3ri_user_email') || '';
  const userPhoto = localStorage.getItem('d3ri_user_photo') || '';

  const handleSignOut = async () => {
    try { await signOut(auth); } catch { /* ignore */ }
    localStorage.removeItem('d3ri_logged_in');
    localStorage.removeItem('d3ri_user_email');
    localStorage.removeItem('d3ri_user_name');
    localStorage.removeItem('d3ri_user_photo');
    localStorage.removeItem('d3ri_selected_role');
    navigate('/login');
  };

  return (
    <nav style={styles.nav}>
      <div style={styles.logo}>
        <span style={styles.logoText}>D3RI</span>
        <span style={styles.subtitle}>Clinical Trial Intelligence</span>
      </div>
      {role && (
        <div style={styles.roleBanner}>
          {role === 'coordinator' ? '\uD83D\uDCCB' : role === 'monitor' ? '\uD83D\uDD0D' : role === 'budget' ? '\uD83D\uDCB2' : '\uD83D\uDD11'}
          {' '}
          {role === 'coordinator' ? 'Site Coordinator' : role === 'monitor' ? 'Clinical Monitor' : role === 'budget' ? 'Budget Coordinator' : 'Admin'}
        </div>
      )}
      <div style={styles.links}>
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            style={({ isActive }) => ({
              ...styles.link,
              backgroundColor: isActive ? '#1a365d' : 'transparent',
              color: isActive ? '#fff' : '#a0aec0',
            })}
          >
            <span style={styles.icon}>{item.icon}</span>
            {item.label}
            {item.badge > 0 && (
              <span style={styles.badge}>{item.badge}</span>
            )}
          </NavLink>
        ))}
      </div>
      <div style={styles.footer}>
        {(userName || userEmail) && (
          <div style={styles.userInfo}>
            {userPhoto ? (
              <img src={userPhoto} alt="" style={styles.userPhoto} referrerPolicy="no-referrer" />
            ) : (
              <div style={styles.userAvatar}>{(userName || userEmail).charAt(0).toUpperCase()}</div>
            )}
            <div style={styles.userDetails}>
              {userName && <div style={styles.userName}>{userName}</div>}
              {userEmail && <div style={styles.userEmail}>{userEmail}</div>}
            </div>
          </div>
        )}
        <button style={styles.signOutBtn} onClick={handleSignOut}>Sign out</button>
        <span style={styles.version}>MVP v0.1.0</span>
      </div>
    </nav>
  );
}

const styles: Record<string, React.CSSProperties> = {
  nav: {
    width: 240, minHeight: '100vh', backgroundColor: '#0f1b2d',
    display: 'flex', flexDirection: 'column', padding: '0',
    borderRight: '1px solid #1a365d',
  },
  logo: {
    padding: '24px 20px', borderBottom: '1px solid #1a365d',
    display: 'flex', flexDirection: 'column',
  },
  logoText: { fontSize: 28, fontWeight: 800, color: '#63b3ed', letterSpacing: 2 },
  subtitle: { fontSize: 11, color: '#718096', marginTop: 4 },
  roleBanner: {
    padding: '8px 20px', fontSize: 11, fontWeight: 600, color: '#a0aec0',
    backgroundColor: '#171923', borderBottom: '1px solid #1a365d',
    letterSpacing: 0.3,
  },
  links: { flex: 1, paddingTop: 16 },
  link: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '12px 20px', textDecoration: 'none',
    fontSize: 14, borderRadius: 0, transition: 'all 0.15s',
  },
  icon: { fontSize: 16 },
  badge: {
    backgroundColor: '#f56565', color: '#fff', fontSize: 10, fontWeight: 700,
    padding: '1px 6px', borderRadius: 8, marginLeft: 'auto',
  },
  footer: { padding: '16px 20px', borderTop: '1px solid #1a365d' },
  userInfo: {
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
  },
  userPhoto: {
    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
  },
  userAvatar: {
    width: 32, height: 32, borderRadius: '50%', backgroundColor: '#2d3748',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, fontWeight: 700, color: '#a0aec0', flexShrink: 0,
  },
  userDetails: {
    overflow: 'hidden',
  },
  userName: {
    fontSize: 12, fontWeight: 600, color: '#e2e8f0',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  userEmail: {
    fontSize: 10, color: '#718096', marginTop: 1,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  signOutBtn: {
    width: '100%', padding: '8px 0', marginBottom: 12,
    backgroundColor: 'transparent', border: '1px solid #2d3748',
    borderRadius: 6, color: '#a0aec0', fontSize: 12, cursor: 'pointer',
    transition: 'all 0.15s', fontFamily: 'inherit',
  },
  version: { fontSize: 11, color: '#4a5568' },
};
